import React, { useId, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import Banner from '../../common/Banner';
import { validateUploadFile } from '../../../services/uploads/presignedUpload';

export interface SignedCopyUploadProps {
  /** Uploads the file (ticket → S3 → confirm). Reject to show the error. */
  onUpload: (file: File, onProgress: (pct: number) => void) => Promise<void>;
  disabled?: boolean;
  label?: string;
  /** ORD-07 guidance (default on) */
  showSignatureGuidance?: boolean;
}

/**
 * Upload control for a physician-signed copy (POC, supplemental orders). Uses the pre-signed
 * upload path; shows real progress and the ORD-07 "no stamps" guidance.
 */
const SignedCopyUpload: React.FC<SignedCopyUploadProps> = ({
  onUpload,
  disabled = false,
  label = 'Upload signed copy',
  showSignatureGuidance = true,
}) => {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    const invalid = validateUploadFile(file);
    if (invalid) {
      setError(invalid);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setProgress(0);
    try {
      await onUpload(file, (pct) => setProgress(pct));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'The upload failed. Try again.');
    } finally {
      setProgress(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const uploading = progress !== null;

  return (
    <div className="space-y-2" data-testid="signed-copy-upload">
      {showSignatureGuidance && (
        <Banner variant="warning" data-testid="signature-guidance">
          Physicians must sign; stamps are not accepted.
        </Banner>
      )}
      <label
        htmlFor={inputId}
        className={`inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 ${
          disabled || uploading ? 'pointer-events-none opacity-50' : ''
        }`}
      >
        <Upload size={16} aria-hidden="true" />
        {uploading ? 'Uploading…' : label}
      </label>
      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png"
        className="sr-only"
        aria-label={label}
        disabled={disabled || uploading}
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />
      <p className="text-xs text-slate-500">PDF, JPG or PNG, up to 10 MB.</p>
      {uploading && (
        <div className="h-2 w-full overflow-hidden rounded bg-slate-200" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress ?? 0}>
          <div className="h-2 bg-slate-700 transition-all" style={{ width: `${progress ?? 0}%` }} />
        </div>
      )}
      {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
    </div>
  );
};

export default SignedCopyUpload;
